import {
	GetAllFaqCategoriesWithQuestionsSlugsQuery,
	useGetAllFaqCategoriesWithQuestionsSlugsQuery,
} from '@/api/sanity/generated';
import { sanityLanguage } from '@/constants/language';
import { createCache } from '@/utils/cache';

const MAX_CACHE_SIZE = 10000;

type SlugsList = Array<{ category: string; question: string }>;

const cache = createCache<NonNullable<SlugsList>[number]>(MAX_CACHE_SIZE, 'faq-slugs');
const categoryCache = createCache<
	NonNullable<GetAllFaqCategoriesWithQuestionsSlugsQuery['allFaqCategory']>[number]
>(MAX_CACHE_SIZE, 'faq-category-slugs');

export async function getAllCategoriesSlugs(): Promise<{
	categoriesSlugs: Array<
		NonNullable<GetAllFaqCategoriesWithQuestionsSlugsQuery['allFaqCategory']>[number]
	>;
	cache: typeof categoryCache;
}> {
	if (categoryCache.size > 0) {
		return new Promise((resolve) => {
			resolve({
				categoriesSlugs: Array.from(categoryCache.values()),
				cache: categoryCache,
			});
		});
	}

	const fetchAllCategories = useGetAllFaqCategoriesWithQuestionsSlugsQuery.fetcher({
		i18nLang: sanityLanguage,
	});

	const categories = await fetchAllCategories();

	const { allFaqCategory } = categories;

	for (let index = 0; index < allFaqCategory.length; index++) {
		const category = allFaqCategory[index];

		if (category?.slug?.current) {
			categoryCache.set(category.slug.current, category, 48 * 60 * 60 * 1000); // 48 hours
		}
	}

	return {
		categoriesSlugs: allFaqCategory,
		cache: categoryCache,
	};
}

export async function getAllQuestionSlugs(): Promise<{
	questionsSlugs: SlugsList;
	cache: typeof cache;
}> {
	if (cache.size > 0) {
		return new Promise((resolve) => {
			resolve({
				questionsSlugs: Array.from(cache.values()),
				cache,
			});
		});
	}

	const fetchAllCategories = useGetAllFaqCategoriesWithQuestionsSlugsQuery.fetcher({
		i18nLang: sanityLanguage,
	});

	const categories = await fetchAllCategories();

	const { allFaqCategory } = categories;

	const questionsSlugs: Array<{
		category: string;
		question: string;
	}> = [];

	for (let index = 0; index < allFaqCategory.length; index++) {
		const category = allFaqCategory[index];

		if (!category?.questions) continue;

		if (!category?.slug?.current) continue;

		for (let index = 0; index < category?.questions?.length; index++) {
			const question = category.questions[index];

			if (question?.slug?.current && category?.slug?.current) {
				const slugPair = {
					category: category.slug.current,
					question: question.slug.current,
				};

				questionsSlugs.push(slugPair);

				// Use composite key to avoid overwriting duplicate questions across categories
				const compositeKey = `${category.slug.current}/${question.slug.current}`;

				cache.set(compositeKey, slugPair, 48 * 60 * 60 * 1000); // 48 hours
			}
		}
	}

	return {
		questionsSlugs,
		cache,
	};
}
